import type { LoaderParser, TextureSourceOptions } from 'pixi.js';
import { ExtensionType, Texture } from 'pixi.js';

const MAX_AVATAR_WIDTH = 150;
const MAX_AVATAR_HEIGHT = 150;

const resizeCanvas = document.createElement('canvas');
const resizeContext = resizeCanvas.getContext('2d');

/**
 * Parse and load avatar URLs from Facebook. This will load images with "anonymous" cross-origin attribute to prevent
 * tainting the game canvas. Images will also be drawn into a canvas and resized before loaded in PIXI. This is
 * recommended by FB and prevents us from caching/rendering very high-res textures unnecessarily.
 */
const facebookAvatarParser = {
  extension: ExtensionType.LoadParser,

  test(url: string): boolean {
    return url.includes('https://platform-lookaside.fbsbx.com/platform/profilepic/');
  },

  async load(url: string): Promise<Texture> {
    let src: TextureSourceOptions | null = null;

    src = await new Promise((resolve) => {
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.src = url;

      const resizeImage = (): void => {
        if (resizeContext === null) {
          // Context unavailable - resolve with original image.
          resolve(image);
          return;
        }

        let { width, height } = image;

        // Shrink image maintaining aspect ratio.
        if (width > height) {
          if (width > MAX_AVATAR_WIDTH) {
            height *= MAX_AVATAR_WIDTH / width;
            width = MAX_AVATAR_WIDTH;
          }
        } else if (height > MAX_AVATAR_HEIGHT) {
          width *= MAX_AVATAR_HEIGHT / height;
          height = MAX_AVATAR_HEIGHT;
        }

        resizeCanvas.width = width;
        resizeCanvas.height = height;
        resizeContext.drawImage(image, 0, 0, width, height);
        const resizedImage = new Image();
        resizedImage.src = resizeCanvas.toDataURL();
        if (resizedImage.complete) {
          resolve(resizedImage);
        } else {
          resizedImage.onload = (): void => {
            resolve(resizedImage);
          };
        }
      };

      if (image.complete) {
        resizeImage();
      } else {
        image.onload = (): void => {
          resizeImage();
        };
      }
    });

    return src ? Texture.from(src) : new Texture();
  },

  unload(texture: Texture): void {
    texture.destroy(true);
  },
} as LoaderParser<Texture>;

export default facebookAvatarParser;
